<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useConsoleLogger } from '@/composables/useConsoleLogger';
import { SButton, SButtonGroup, SButtonGroupRadio, SCard, SFormCheckbox, SStack, SText } from '@sysarb/shared-style';
import Modal from './common/Modal.vue';

const {
    toggleCategory,
    isCategoryEnabled,
    toggleConfigurations,
    resetCategoryToggles,
    loggingMode,
    setLoggingMode,
} = useConsoleLogger();

const showModal = ref(false);

function defineCategoryModel(key: string): ReturnType<typeof computed<boolean>> {
    return computed({
        get: () => isCategoryEnabled(key),
        set: (value: boolean) => toggleCategory(key, value)
    });
}

// Create a plain object with computed refs for v-model binding
const categoryModels: Record<string, ReturnType<typeof defineCategoryModel>> = {};

function resetAllToggles(): void {
    resetCategoryToggles();
}

function toggleModal(): void {
    showModal.value = !showModal.value;
}

function openModal(): void {
    showModal.value = true;
}

function closeModal(): void {
    showModal.value = false;
}

// Expose consoleControl function to window
interface ConsoleControl {
    (): void;
    open: () => void;
    close: () => void;
    toggle: () => void;
}

declare global {
    interface Window {
        consoleControl?: ConsoleControl;
    }
}

onMounted(() => {
    const control: ConsoleControl = Object.assign(toggleModal, {
        open: openModal,
        close: closeModal,
        toggle: toggleModal
    });
    window.consoleControl = control;

    // Initialize models for all categories
    for (const config of toggleConfigurations) {
        categoryModels[config.key] = defineCategoryModel(config.key);
    }
});

onUnmounted(() => {
    delete window.consoleControl;
});
</script>

<template>
    <Modal
        v-if="showModal"
        :show-footer="false"
        header="🎛️ Console Logger Control Panel"
        @close="closeModal"
    >
        <SStack vertical gap="5" align-items="start">
            <SStack gap="5" vertical class="pb-5">
                <SStack gap="5">
                    <label class="mode-toggle-label">Logging Mode:</label>
                    <SButtonGroup name="logging-mode">
                        <SButtonGroupRadio
                            id="logging-mode-simple"
                            label="Simple"
                            value="simple"
                            :checked="loggingMode === 'simple'"
                            @change="setLoggingMode"
                        />
                        <SButtonGroupRadio
                            id="logging-mode-verbose"
                            label="Verbose"
                            value="verbose"
                            :checked="loggingMode === 'verbose'"
                            @change="setLoggingMode"
                        />
                    </SButtonGroup>
                    <SText size="5">
                        Simple: <code>cat emoji message</code>
                        Verbose: <code>cat emoji [file:line:column] \n message</code>
                    </SText>
                </SStack>
            </SStack>
            <SStack wrap gap="5" align-items="stretch">
                <SCard
                    v-for="config in toggleConfigurations"
                    :key="config.key"
                    :class="[{ 'active': isCategoryEnabled(config.key) }]"
                >
                    <SStack gap="3">
                        <SFormCheckbox
                            :id="config.key"
                            :label="`${config.emoji} ${config.key}`"
                            v-model="categoryModels[config.key].value"
                        />
                    </SStack>
                    <SText size="5">{{ config.description }}</SText>
                </SCard>
            </SStack>
            <SButton variant="secondary-light" @click="resetAllToggles">
                Reset to Defaults
            </SButton>
        </SStack>
    </Modal>
</template>

<style scoped>

.toggle-item.active {
    border-color: transparent;
    background-color:var(--sy-component-active-bg);
}
</style>
